import { useRef, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { isApiError } from "@bw/shared/api";
import type { BookingInvitationResponse } from "@bw/shared/types/generated";
import { useCancelBookingInvitation } from "../hooks/useCancelBookingInvitation";

/**
 * The host's per-invitee Cancel control: an inline "Cancel invite" button that
 * opens a confirm dialog before the destructive call. On confirm it calls
 * useCancelBookingInvitation(facilityId, bookingId, invitationId); the
 * invitations list REFETCHES on success (never an optimistic local removal). A
 * failure surfaces a role="alert" and KEEPS the dialog open.
 *
 * Mirrors RemoveParticipantControl exactly: trigger → dialog → confirm
 * ("Yes, cancel") / keep ("Keep invite") → invalidate query. MUI owns the modal
 * focus trap while the safe, non-destructive button receives initial focus.
 *
 * Mounted by the page ONLY for a host viewer on a pending InvitedRow, so this
 * control is never a dead affordance the backend would reject.
 */
export function CancelInvitationControl({
  facilityId,
  bookingId,
  invitation,
}: {
  facilityId: string;
  bookingId: string;
  invitation: BookingInvitationResponse;
}) {
  const [confirming, setConfirming] = useState(false);
  const cancel = useCancelBookingInvitation(facilityId, bookingId);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const name = invitation.invitee_display_name?.trim() || "this player";

  function open() {
    cancel.reset();
    setConfirming(true);
  }

  function close() {
    if (cancel.isPending) return;
    setConfirming(false);
    triggerRef.current?.focus();
  }

  function handleConfirm() {
    cancel.mutate(invitation.invitation_id, {
      onSuccess: () => {
        setConfirming(false);
        triggerRef.current?.focus();
      },
      // On error the dialog stays open and surfaces cancel.error.
    });
  }

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="text"
        size="small"
        onClick={open}
        aria-label={`Cancel invite for ${name}`}
      >
        Cancel invite
      </Button>

      <Dialog
        open={confirming}
        onClose={() => close()}
        aria-labelledby="cancel-invitation-title"
        aria-describedby="cancel-invitation-description"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle id="cancel-invitation-title">
          Cancel invite for {name}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-invitation-description">
            They&apos;ll no longer receive this court invitation.
          </DialogContentText>
          {cancel.isError ? (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              {cancelErrorMessage(cancel.error)}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            type="button"
            variant="text"
            size="small"
            onClick={close}
            disabled={cancel.isPending}
          >
            Keep invite
          </Button>
          <Button
            type="button"
            color="error"
            variant="contained"
            size="small"
            onClick={handleConfirm}
            disabled={cancel.isPending}
          >
            {cancel.isPending ? "Cancelling…" : "Yes, cancel"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** Map a cancel rejection to an honest message. */
function cancelErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    if (error.status === 404) {
      return "We couldn't find that invitation — it may have already been cancelled.";
    }
    if (error.status === 409) {
      return "This invitation can no longer be cancelled.";
    }
    return (
      error.message || "We couldn't cancel that invitation. Please try again."
    );
  }
  return "We couldn't cancel that invitation. Please try again.";
}
